import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import Logo from '@/components/Logo'
import { Display, H2, H3, H4, Lead, Small } from '@/components/ui/typography'
import { useTranslation } from 'react-i18next'
import { useLang } from '@/hooks/useLang'
import { generateSeo } from '@/lib/seo'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import {
  ChartLineIcon,
  AirplaneTiltIcon,
  ShieldWarningIcon,
  SoccerBallIcon,
  NewspaperIcon,
  HeartbeatIcon,
  BriefcaseIcon,
  MusicNoteIcon,
  StarIcon,
  ArrowRightIcon,
} from '@phosphor-icons/react'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'
import OpenClawIcon from '@/assets/openclaw-color.svg?react'
import OpenClawText from '@/assets/openclaw-text.svg?react'

export const Route = createFileRoute('/$lang/openclaw/use-cases/')({
  component: UseCasesIndexPage,
  head: ({ match }) => {
    const { meta, links } = generateSeo({
      title: 'OpenClaw Voice Use Cases — PhoneSalad',
      description: 'Explore how AI voice agents are being used across finance, travel, health, business, and more. Real examples of what your OpenClaw agent can call about.',
      pathname: match.pathname,
    })
    return { meta, links }
  },
})

const CATEGORIES: {
  key: string
  icon: PhosphorIcon
  color: string
  bg: string
}[] = [
  { key: 'finance', icon: ChartLineIcon, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  { key: 'travel', icon: AirplaneTiltIcon, color: 'text-sky-600', bg: 'bg-sky-100 dark:bg-sky-900/30' },
  { key: 'emergency', icon: ShieldWarningIcon, color: 'text-rose-600', bg: 'bg-rose-100 dark:bg-rose-900/30' },
  { key: 'sports', icon: SoccerBallIcon, color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  { key: 'news', icon: NewspaperIcon, color: 'text-violet-600', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  { key: 'health', icon: HeartbeatIcon, color: 'text-pink-600', bg: 'bg-pink-100 dark:bg-pink-900/30' },
  { key: 'business', icon: BriefcaseIcon, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  { key: 'entertainment', icon: MusicNoteIcon, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30' },
]

function UseCasesIndexPage() {
  const { t } = useTranslation('openclaw')
  const lang = useLang()

  // Gather highlighted examples from all categories
  const highlighted: { categoryKey: string; title: string; description: string; color: string; bg: string; icon: PhosphorIcon }[] = []
  for (const cat of CATEGORIES) {
    const examples = t(`openclaw.useCasesPage.categories.${cat.key}.examples`, { returnObjects: true }) as { title: string; description: string; highlighted?: string }[]
    for (const ex of examples) {
      if (ex.highlighted) {
        highlighted.push({ categoryKey: cat.key, title: ex.title, description: ex.description, color: cat.color, bg: cat.bg, icon: cat.icon })
      }
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="py-20 lg:py-28 px-4">
        <div className="container mx-auto max-w-4xl">
          <Link
            to="/$lang/openclaw"
            params={{ lang }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> {t('openclaw.breadcrumb')}
          </Link>
          <div className="text-center">
          <div className="flex items-center gap-3 justify-center mb-6">
            <div className="flex items-center gap-1.5">
              <OpenClawIcon className="w-6 h-6" />
              <OpenClawText className="h-4 text-foreground" />
            </div>
            <span className="text-muted-foreground font-medium text-sm">x</span>
            <div className="flex items-center gap-1.5">
              <Logo size="sm" linkTo="" />
            </div>
          </div>
          <Display className="mb-6">{t('openclaw.useCasesPage.headline')}</Display>
          <Lead className="max-w-2xl mx-auto">{t('openclaw.useCasesPage.description')}</Lead>
          </div>
        </div>
      </section>

      {/* Popular Examples */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-2 mb-8">
            <StarIcon weight="duotone" className="w-6 h-6 text-amber-500" />
            <H2>{t('openclaw.useCasesPage.popularTitle')}</H2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {highlighted.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={`${item.categoryKey}-${item.title}`}
                  to="/$lang/openclaw/use-cases/$category"
                  params={{ lang, category: item.categoryKey }}
                  className="group"
                >
                  <Card variant="outline" className="h-full transition-shadow hover:shadow-md">
                    <CardContent className="flex flex-col h-full">
                      <div className={`w-10 h-10 rounded-lg ${item.bg} flex items-center justify-center mb-4`}>
                        <Icon weight="duotone" className={`w-5 h-5 ${item.color}`} />
                      </div>
                      <H4 className="mb-2 leading-snug agent-quote">{item.title}</H4>
                      <Small className="text-muted-foreground mt-auto">{item.description}</Small>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* All Categories */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <H2 className="mb-12 text-center">{t('openclaw.useCasesPage.browseCategories')}</H2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon
              return (
                <Link
                  key={cat.key}
                  to="/$lang/openclaw/use-cases/$category"
                  params={{ lang, category: cat.key }}
                  className="group"
                >
                  <Card variant="outline" className="h-full transition-all hover:shadow-md hover:border-primary/30">
                    <CardContent className="flex flex-col items-center text-center py-8">
                      <div className={`w-14 h-14 rounded-2xl ${cat.bg} flex items-center justify-center mb-4`}>
                        <Icon weight="duotone" className={`w-7 h-7 ${cat.color}`} />
                      </div>
                      <H3 className="mb-2">{t(`openclaw.useCasesPage.categories.${cat.key}.name`)}</H3>
                      <Small className="text-muted-foreground">{t(`openclaw.useCasesPage.categories.${cat.key}.description`)}</Small>
                      <div className="mt-4 text-primary flex items-center gap-1 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        View examples <ArrowRightIcon weight="bold" className="w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-2xl text-center">
          <H2 className="mb-4">{t('openclaw.cta.title')}</H2>
          <Lead className="mb-8">{t('openclaw.cta.description')}</Lead>
          <Button size="lg" asChild>
            <Link to="/$lang/openclaw/setup" params={{ lang }}>
              {t('openclaw.cta.button')} <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
